/**
 * MemoryManagerPage — 记忆管理中心
 *
 * 3-layer memory architecture:
 * - Task Memory: per-conversation/job short-term context
 * - Workspace Memory: per-tenant shared memory (facts, procedures, contacts)
 * - Organization Memory: cross-tenant global policies
 *
 * Features:
 * - Browse & search memory entries per layer
 * - View memory details (key, value, metadata, TTL)
 * - Delete / expire memory entries
 * - Stats per layer (count, size, avg TTL)
 *
 * API (memory lives in packages/mastra-agents/src/memory-store.ts):
 *   GET    /ai-employees/:id/metrics   (includes memory stats)
 *   We also provide a lightweight in-page REST wrapper
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Brain,
  Search,
  Loader2,
  Trash2,
  RefreshCw,
  Clock,
  Database,
  Layers,
  Server,
  Globe,
  Briefcase,
  AlertCircle,
  ChevronRight,
  BarChart3,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────

interface MemoryEntry {
  id: string;
  layer: 'task' | 'workspace' | 'organization';
  key: string;
  value: unknown;
  metadata: Record<string, unknown>;
  ttl_seconds: number | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
}

interface MemoryStats {
  layer: string;
  count: number;
  oldest: string | null;
  newest: string | null;
}

type LayerFilter = 'all' | 'task' | 'workspace' | 'organization';

const LAYER_CONFIG = {
  task: { labelKey: 'admin.memory.taskMemory', icon: Briefcase, color: 'text-primary', bg: 'bg-primary/10' },
  workspace: { labelKey: 'admin.memory.workspaceMemory', icon: Server, color: 'text-green-600', bg: 'bg-green-500/10' },
  organization: { labelKey: 'admin.memory.orgMemory', icon: Globe, color: 'text-purple-600', bg: 'bg-purple-500/10' },
};

// ─── Component ─────────────────────────────────────

export default function MemoryManagerPage() {
  const { t } = useTranslation();

  const [entries, setEntries] = useState<MemoryEntry[]>([]);
  const [stats, setStats] = useState<MemoryStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [layerFilter, setLayerFilter] = useState<LayerFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Set<string>>(new Set());

  // ─── Load memory data ────────────────────────────

  const loadMemory = useCallback(async () => {
    setLoading(true);
    try {
      // Try to load memory entries from the agent memory API
      const [entriesRes, statsRes] = await Promise.allSettled([
        api.get<MemoryEntry[]>(`/memory/entries?layer=${layerFilter === 'all' ? '' : layerFilter}&limit=100`),
        api.get<MemoryStats[]>('/memory/stats'),
      ]);

      if (entriesRes.status === 'fulfilled') {
        setEntries(entriesRes.value);
      } else {
        // Mock / empty data if API not yet available
        setEntries([]);
      }

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value);
      } else {
        setStats([]);
      }
    } finally {
      setLoading(false);
    }
  }, [layerFilter]);

  useEffect(() => {
    loadMemory();
  }, [loadMemory]);

  // ─── Delete entry ────────────────────────────────

  const handleDelete = async (id: string) => {
    setDeleting((prev) => new Set(prev).add(id));
    try {
      await api.delete(`/memory/entries/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Filter entries
  const filtered = entries.filter((e) => {
    if (layerFilter !== 'all' && e.layer !== layerFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        e.key.toLowerCase().includes(q) ||
        JSON.stringify(e.value).toLowerCase().includes(q)
      );
    }
    return true;
  });

  // ─── Render ──────────────────────────────────────

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      {/* Header */}
      <Card>
        <CardContent className="py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="h-5 w-5 text-primary" />
            <h1 className="font-semibold">{t('admin.memory.title')}</h1>
            <span className="text-xs text-muted-foreground">{t('admin.memory.subtitle')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadMemory}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              {t('admin.refresh')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-3">
        {(['task', 'workspace', 'organization'] as const).map((layer) => {
          const cfg = LAYER_CONFIG[layer];
          const Icon = cfg.icon;
          const stat = stats.find((s) => s.layer === layer);
          return (
            <Card key={layer} className={cn('cursor-pointer transition-colors', layerFilter === layer && 'ring-1 ring-primary')} onClick={() => setLayerFilter(layerFilter === layer ? 'all' : layer)}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={cn('rounded p-1.5', cfg.bg)}>
                      <Icon className={cn('h-4 w-4', cfg.color)} />
                    </div>
                    <div>
                      <p className="text-xs font-medium">{t(cfg.labelKey)}</p>
                      <p className="text-lg font-bold">{stat?.count ?? 0}</p>
                    </div>
                  </div>
                  {stat?.newest && (
                    <span className="text-[10px] text-muted-foreground">
                      {t('admin.memory.latest')}: {new Date(stat.newest).toLocaleDateString('zh-CN')}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search + filter */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={t('admin.memory.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <div className="flex gap-1">
          {(['all', 'task', 'workspace', 'organization'] as LayerFilter[]).map((f) => (
            <Button
              key={f}
              variant={layerFilter === f ? 'default' : 'ghost'}
              size="sm"
              className="text-xs"
              onClick={() => setLayerFilter(f)}
            >
              {f === 'all' ? t('admin.all') : t(LAYER_CONFIG[f].labelKey)}
            </Button>
          ))}
        </div>
        <Badge variant="outline" className="text-xs ml-auto">
          {filtered.length} {t('admin.memory.records', { count: filtered.length })}
        </Badge>
      </div>

      {/* Entries list */}
      <div className="flex-1 overflow-auto space-y-1.5">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Brain className="h-10 w-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm">{t('admin.memory.noRecords')}</p>
            <p className="text-xs mt-1">{t('admin.memory.noRecordsHint')}</p>
          </div>
        ) : (
          filtered.map((entry) => {
            const cfg = LAYER_CONFIG[entry.layer];
            const Icon = cfg.icon;
            const isExpired = entry.expires_at ? new Date(entry.expires_at) < new Date() : false;
            return (
              <Card key={entry.id} className={cn(isExpired && 'opacity-50')}>
                <CardContent className="py-2 px-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}>
                        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expandedId === entry.id && 'rotate-90')} />
                      </button>
                      <Icon className={cn('h-3.5 w-3.5', cfg.color)} />
                      <span className="text-xs font-mono font-medium">{entry.key}</span>
                      <Badge variant="outline" className={cn('text-[10px]', cfg.color)}>
                        {t(cfg.labelKey)}
                      </Badge>
                      {isExpired && (
                        <Badge variant="outline" className="text-[10px] text-red-500">{t('admin.memory.expired')}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.ttl_seconds != null && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          TTL: {entry.ttl_seconds}s
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(entry.updated_at).toLocaleString('zh-CN')}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500"
                        onClick={() => handleDelete(entry.id)}
                        disabled={deleting.has(entry.id)}
                      >
                        {deleting.has(entry.id) ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                  {expandedId === entry.id && (
                    <div className="mt-2 pl-5 space-y-2">
                      <div>
                        <label className="text-[10px] text-muted-foreground">{t('admin.memory.value')}</label>
                        <pre className="text-xs bg-muted/30 rounded p-2 max-h-40 overflow-auto font-mono">
                          {typeof entry.value === 'string' ? entry.value : JSON.stringify(entry.value, null, 2)}
                        </pre>
                      </div>
                      {Object.keys(entry.metadata).length > 0 && (
                        <div>
                          <label className="text-[10px] text-muted-foreground">{t('admin.memory.metadata')}</label>
                          <pre className="text-xs bg-muted/30 rounded p-2 max-h-24 overflow-auto font-mono">
                            {JSON.stringify(entry.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
